import React from 'react';
import { motion } from 'framer-motion';
/**
 * ScoreDot — single answer indicator (§20.5 G4).
 *
 * - goed: gevulde emerald cirkel met zachte glow
 * - fout: cirkel met rose-ring + dunne ✕ (geen rood-alarm, geen skeleton-grijs)
 *
 * Tikbaar: opent (later) Review-screen op deze vraag.
 */
interface ScoreDotProps {
  isCorrect: boolean;
  index: number;
  delay?: number;
  onClick?: () => void;
  size?: 'sm' | 'md';
}
export function ScoreDot({
  isCorrect,
  index,
  delay = 0,
  onClick,
  size = 'md'
}: ScoreDotProps) {
  const dim = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const ariaLabel = `Vraag ${index + 1}: ${isCorrect ? 'goed' : 'fout'}${onClick ? ', tik voor uitleg' : ''}`;
  return <motion.button type="button" onClick={onClick} disabled={!onClick} aria-label={ariaLabel} initial={{
    scale: 0
  }} animate={{
    scale: 1
  }} transition={{
    type: 'spring',
    delay,
    stiffness: 400,
    damping: 22
  }} whileHover={onClick ? {
    scale: 1.15
  } : undefined} whileTap={onClick ? {
    scale: 0.92
  } : undefined} className={`relative ${dim} rounded-full transition-shadow ${onClick ? 'cursor-pointer' : 'cursor-default'} ${isCorrect ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.45)] ring-2 ring-emerald-100' : 'border-[1.5px] border-rose-300 bg-white'}`} data-id="element-11227">
      {!isCorrect && <span className="absolute inset-0 flex items-center justify-center text-rose-400" aria-hidden data-id="element-11228">
          <svg viewBox="0 0 10 10" className={size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5'} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" data-id="element-11229">
            <path d="M2 2 L8 8 M8 2 L2 8" data-id="element-11230" />
          </svg>
        </span>}
    </motion.button>;
}
/**
 * ScoreDotRow — convenience wrapper that lays out a row of dots with
 * a standard stagger and optional onDotClick handler.
 */
interface ScoreDotRowProps {
  results: {
    isCorrect: boolean;
  }[];
  onDotClick?: (index: number) => void;
  baseDelay?: number;
  size?: 'sm' | 'md';
}
export function ScoreDotRow({
  results,
  onDotClick,
  baseDelay = 0.1,
  size = 'md'
}: ScoreDotRowProps) {
  return <div className="flex flex-wrap justify-center gap-1.5" data-id="element-11231">
      {results.map((r, i) => <ScoreDot key={i} index={i} isCorrect={r.isCorrect} delay={baseDelay + i * 0.04} size={size} onClick={onDotClick ? () => onDotClick(i) : undefined} data-id="element-11232" />)}
    </div>;
}